"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import Button from "../../../Common/Buttons/Buttons";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useSelector } from "react-redux";
import Loader from "../../../Loader/Loader";
import useFindDoctorsAccess from "@/hooks/useFindDoctorsAccess";
import { useSidebar } from "@/context/SidebarContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipArrow } from "@/Components/ui/tooltip";

const RepsSidebar = ({ onCloseMobile }) => {
  const pathname = usePathname();
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState("");
  const totalUnreadCount = useSelector(
    (state) => state?.chat?.totalUnreadCount
  );
  const { enabled: isFindDoctorsEnabled, checking: checkingAccess, error: accessError } = useFindDoctorsAccess();

  // Get close function from context or prop
  let sidebarContext = null;
  try {
    sidebarContext = useSidebar();
  } catch (e) {
    // Context not available
  }
  const closeSidebar = onCloseMobile || sidebarContext?.closeMobileSidebar;

  const handleNavigation = (href) => {
    // Prevent navigation if already on the target page
    if (pathname === href) {
      closeSidebar?.(); // Close sidebar on mobile
      return;
    }
    setIsNavigating(true);
    setNavigatingTo(href);
    closeSidebar?.(); // Close sidebar on mobile after navigation
    setTimeout(() => {
      router.push(href);
    }, 100);
  };
  
  useEffect(() => {
    // Reset loading state when pathname changes
    setIsNavigating(false);
    setNavigatingTo("");
  }, [pathname]);

  return (
    <>
      {isNavigating && <Loader />}
      <div className="h-screen w-[250px] px-4 pt-[22px] bg-white flex flex-col">
        <a onClick={(e) => {
          e.preventDefault();
          handleNavigation("/");
        }}>
        <div className="cursor-pointer ml-1">
        <Image src={"/Images/AlphaMdLogo.png"} width={154} height={30} alt="logo" />
        <p className="text-[#7C7C8D] text-[8px] font-semibold tracking-[0.15em] mt-1.5">EVIDENCE &bull; EXPERIENCE &bull; OUTCOMES</p>
          <div className="flex items-center gap-2">
            <span className="text-[#7C7C8D] text-[8px] font-semibold tracking-[0.15em] relative -top-[2px]">INTELLIGENCE BY</span>
            <Image src="/Images/website/mdReviewer.svg" width={80} height={16} alt="MDReviewer" className="h-[44px] w-auto object-contain" />
          </div>
          </div>
        </a>
        <div className="pt-4 w-full flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col justify-between pb-4">
         
          <div className="">
          <p className="pb-4 text-[#8E8E8E] text-[12px] font-medium font-poppins">
            Menu
          </p>
            <a
              className={`space-y-2 link ${pathname === "/reps/dashboard" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("/reps/dashboard");
              }}
            >
              <div
                className={`flex cursor-pointer group transition-all items-center py-3 px-4 gap-4 rounded-lg ${
                  pathname === "/reps/dashboard"
                    ? "bg-[#354EF0] text-white"
                    : "bg-white hover:bg-[#354EF00A]"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transition-all ${
                    pathname === "/reps/dashboard"
                      ? "fill-white"
                      : "fill-[#8E8E8E] group-hover:fill-[#1E5EFF]"
                  }`}
                >
                  <g clipPath="url(#clip0_647_4160)">
                    <path
                      d="M7.70828 6.66672H1.45828C0.654144 6.66672 0 6.01257 0 5.20828V1.45828C0 0.654144 0.654144 0 1.45828 0H7.70828C8.51257 0 9.16672 0.654144 9.16672 1.45828V5.20828C9.16672 6.01257 8.51257 6.66672 7.70828 6.66672ZM1.45828 1.25C1.34338 1.25 1.25 1.34338 1.25 1.45828V5.20828C1.25 5.32333 1.34338 5.41672 1.45828 5.41672H7.70828C7.82333 5.41672 7.91672 5.32333 7.91672 5.20828V1.45828C7.91672 1.34338 7.82333 1.25 7.70828 1.25H1.45828Z"
                      
                    />
                    <path
                      d="M7.70828 20H1.45828C0.654144 20 0 19.3458 0 18.5417V9.79169C0 8.9874 0.654144 8.33325 1.45828 8.33325H7.70828C8.51257 8.33325 9.16672 8.9874 9.16672 9.79169V18.5417C9.16672 19.3458 8.51257 20 7.70828 20ZM1.45828 9.58325C1.34338 9.58325 1.25 9.67664 1.25 9.79169V18.5417C1.25 18.6566 1.34338 18.75 1.45828 18.75H7.70828C7.82333 18.75 7.91672 18.6566 7.91672 18.5417V9.79169C7.91672 9.67664 7.82333 9.58325 7.70828 9.58325H1.45828Z"
                     
                    />
                    <path
                      d="M18.5417 20H12.2917C11.4874 20 10.8333 19.3458 10.8333 18.5417V14.7917C10.8333 13.9874 11.4874 13.3333 12.2917 13.3333H18.5417C19.3458 13.3333 20 13.9874 20 14.7917V18.5417C20 19.3458 19.3458 20 18.5417 20ZM12.2917 14.5833C12.1766 14.5833 12.0833 14.6766 12.0833 14.7917V18.5417C12.0833 18.6566 12.1766 18.75 12.2917 18.75H18.5417C18.6566 18.75 18.75 18.6566 18.75 18.5417V14.7917C18.75 14.6766 18.6566 14.5833 18.5417 14.5833H12.2917Z"
                     
                    />
                    <path
                      d="M18.5417 11.6667H12.2917C11.4874 11.6667 10.8333 11.0126 10.8333 10.2083V1.45828C10.8333 0.654144 11.4874 0 12.2917 0H18.5417C19.3458 0 20 0.654144 20 1.45828V10.2083C20 11.0126 19.3458 11.6667 18.5417 11.6667ZM12.2917 1.25C12.1766 1.25 12.0833 1.34338 12.0833 1.45828V10.2083C12.0833 10.3233 12.1766 10.4167 12.2917 10.4167H18.5417C18.6566 10.4167 18.75 10.3233 18.75 10.2083V1.45828C18.75 1.34338 18.6566 1.25 18.5417 1.25H12.2917Z"
                      
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_647_4160">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span
                  className={`text-[16px] font-light font-poppins ${
                    pathname === "/reps/dashboard" ? "text-white" : "text-[#8E8E8E] group-hover:text-[#1E5EFF]"
                  }`}
                >
                  Dashboard
                </span>
              </div>
            </a>
            <a
              className={`link ${
                pathname === "/reps/messages" ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("/reps/messages");
              }}
            >
              <div
                className={`flex group transition-all cursor-pointer items-center py-3 px-4 gap-4 rounded-lg ${
                  pathname === "/reps/messages"
                    ? "bg-[#354EF0] text-white"
                    : "bg-white hover:bg-[#354EF00A]"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transition-all ${
                    pathname === "/reps/messages"
                      ? "fill-white"
                      : "fill-[#8E8E8E] group-hover:fill-[#1E5EFF]"
                  }`}
                >
                  <g clipPath="url(#clip0_647_4164)">
                    <path
                      d="M19.928 15.6971L18.7664 12.3165C19.3263 11.1719 19.6222 9.8989 19.6242 8.61808C19.6277 6.39101 18.766 4.28526 17.198 2.68877C15.6296 1.09197 13.5397 0.193105 11.3131 0.157831C9.00432 0.121347 6.83416 0.999551 5.20263 2.63104C3.62943 4.20421 2.75685 6.27816 2.72923 8.49441C1.17868 9.66183 0.263482 11.482 0.26649 13.4261C0.267935 14.3358 0.472701 15.2402 0.86071 16.0579L0.060591 18.3864C-0.0769483 18.7867 0.0235207 19.2213 0.322818 19.5206C0.533444 19.7312 0.811139 19.8434 1.09673 19.8434C1.21688 19.8434 1.33845 19.8235 1.457 19.7828L3.78548 18.9827C4.60326 19.3707 5.50759 19.5754 6.41736 19.5769C6.42064 19.5769 6.42377 19.5769 6.42705 19.5769C8.4001 19.5768 10.2341 18.638 11.3973 17.0495C12.6077 17.0176 13.8053 16.7246 14.888 16.1949L18.2686 17.3566C18.4095 17.405 18.5539 17.4286 18.6967 17.4286C19.0361 17.4286 19.3661 17.2953 19.6164 17.0449C19.9721 16.6892 20.0915 16.1728 19.928 15.6971ZM6.42697 18.3864C6.42443 18.3864 6.42174 18.3864 6.4192 18.3864C5.61396 18.3852 4.81431 18.1867 4.10685 17.8124C3.96138 17.7355 3.7906 17.7222 3.63505 17.7756L1.24743 18.596L2.06786 16.2084C2.1213 16.0529 2.10802 15.8821 2.03106 15.7367C1.65677 15.0292 1.45825 14.2295 1.457 13.4243C1.45501 12.129 1.95888 10.9013 2.84001 9.98199C3.12782 11.7365 3.96404 13.3533 5.26021 14.6265C6.54681 15.8902 8.16408 16.6948 9.90909 16.9578C8.98768 17.866 7.74565 18.3864 6.42697 18.3864ZM18.7746 16.2031C18.7407 16.237 18.7006 16.2462 18.6554 16.2306L15.0363 14.987C14.9735 14.9654 14.9081 14.9547 14.8429 14.9547C14.7469 14.9547 14.6512 14.978 14.5646 15.0238C13.5312 15.5705 12.3636 15.8604 11.1877 15.8622C11.1839 15.8622 11.1803 15.8622 11.1765 15.8622C7.2365 15.8622 3.98208 12.6616 3.91966 8.72261C3.88822 6.73882 4.64283 4.8744 6.04443 3.4728C7.44604 2.07119 9.31077 1.31686 11.2943 1.34811C15.2371 1.41065 18.4398 4.67108 18.4338 8.61621C18.4319 9.79207 18.142 10.9598 17.5954 11.993C17.5185 12.1384 17.5052 12.3092 17.5586 12.4648L18.8022 16.0839C18.8177 16.1292 18.8084 16.1693 18.7746 16.2031Z"
                     
                    />
                    <path
                      d="M14.7223 5.44971H7.63015C7.3014 5.44971 7.03491 5.71623 7.03491 6.04494C7.03491 6.37369 7.30144 6.64018 7.63015 6.64018H14.7223C15.0511 6.64018 15.3176 6.37365 15.3176 6.04494C15.3176 5.71623 15.0511 5.44971 14.7223 5.44971Z"
                     
                    />
                    <path
                      d="M14.7223 7.89783H7.63015C7.3014 7.89783 7.03491 8.16435 7.03491 8.49306C7.03491 8.82177 7.30144 9.0883 7.63015 9.0883H14.7223C15.0511 9.0883 15.3176 8.82177 15.3176 8.49306C15.3176 8.16435 15.0511 7.89783 14.7223 7.89783Z"
                      
                    />
                    <path
                      d="M11.9924 10.3458H7.63015C7.3014 10.3458 7.03491 10.6123 7.03491 10.9411C7.03491 11.2698 7.30144 11.5363 7.63015 11.5363H11.9923C12.3211 11.5363 12.5876 11.2698 12.5876 10.9411C12.5876 10.6123 12.3211 10.3458 11.9924 10.3458Z"
                   
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_647_4164">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span
                  className={`text-[16px] font-light font-poppins ${
                    pathname === "/reps/messages"
                      ? "text-white"
                      : "text-[#8E8E8E] group-hover:text-[#1E5EFF]"
                  }`}
                >
                  Messages {totalUnreadCount ? `(${totalUnreadCount})` : ""}
                </span>
              </div>
            </a>
            <a
              className={`link ${
                pathname === "/reps/meetings" ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("/reps/meetings");
              }}
            >
              <div
                className={`flex group transition-all cursor-pointer items-center py-3 px-4 gap-4 rounded-lg ${
                  pathname === "/reps/meetings"
                    ? "bg-[#354EF0] text-white"
                    : "bg-white hover:bg-[#354EF00A]"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className={`transition-all ${
                    pathname === "/reps/meetings"
                      ? "fill-white"
                      : "fill-[#8E8E8E] group-hover:fill-[#1E5EFF]"
                  }`}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.1417 8.375C12.7617 8.375 11.6417 9.495 11.6417 10.875C11.6417 12.255 12.7617 13.375 14.1417 13.375C15.5217 13.375 16.6417 12.255 16.6417 10.875C16.6417 9.495 15.5217 8.375 14.1417 8.375ZM14.1417 9.625C14.8317 9.625 15.3917 10.185 15.3917 10.875C15.3917 11.565 14.8317 12.125 14.1417 12.125C13.4517 12.125 12.8917 11.565 12.8917 10.875C12.8917 10.185 13.4517 9.625 14.1417 9.625Z"
                   
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.7725 17.9583C13.0392 17.9583 15.25 17.9583 16.5167 17.9475C16.9842 17.9475 17.4275 17.7158 17.6983 17.3275C17.9692 16.9392 18.0325 16.4433 17.8692 15.9992C17.325 14.4733 15.8608 13.375 14.1417 13.375C12.425 13.375 10.9617 14.47 10.4033 15.9958C10.2383 16.4433 10.3025 16.9433 10.5758 17.3342C10.8483 17.7258 11.2958 17.9583 11.7725 17.9583ZM11.7725 16.7083C11.7042 16.7083 11.64 16.675 11.6008 16.6192C11.5617 16.5633 11.5525 16.4917 11.5758 16.4275C11.5767 16.4275 11.5767 16.4267 11.5767 16.4267C11.96 15.3792 12.9633 14.625 14.1417 14.625C15.3183 14.625 16.32 15.3767 16.6942 16.425C16.695 16.4275 16.695 16.43 16.6958 16.4317C16.7183 16.4925 16.71 16.56 16.6725 16.6125C16.6359 16.6658 16.5759 16.6975 16.5108 16.6975C16.5092 16.6975 16.5075 16.6975 16.5058 16.6975C15.2417 16.7083 13.0358 16.7083 11.7725 16.7083Z"
                
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.2917 6.49996V4.41663C16.2917 2.69079 14.8925 1.29163 13.1667 1.29163H3.16669C1.44085 1.29163 0.041687 2.69079 0.041687 4.41663V14.4166C0.041687 16.1425 1.44085 17.5416 3.16669 17.5416H8.16669C8.51169 17.5416 8.79169 17.2616 8.79169 16.9166C8.79169 16.5716 8.51169 16.2916 8.16669 16.2916C8.16669 16.2916 5.34835 16.2916 3.16669 16.2916C2.13085 16.2916 1.29169 15.4525 1.29169 14.4166V4.41663C1.29169 3.38079 2.13085 2.54163 3.16669 2.54163H13.1667C14.2025 2.54163 15.0417 3.38079 15.0417 4.41663V6.49996C15.0417 6.84496 15.3217 7.12496 15.6667 7.12496C16.0117 7.12496 16.2917 6.84496 16.2917 6.49996Z"
                  
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4 0.666626V3.16663C4 3.51163 4.28 3.79163 4.625 3.79163C4.97 3.79163 5.25 3.51163 5.25 3.16663V0.666626C5.25 0.321626 4.97 0.041626 4.625 0.041626C4.28 0.041626 4 0.321626 4 0.666626Z"
                  
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.0834 0.666626V3.16663C11.0834 3.51163 11.3634 3.79163 11.7084 3.79163C12.0534 3.79163 12.3334 3.51163 12.3334 3.16663V0.666626C12.3334 0.321626 12.0534 0.041626 11.7084 0.041626C11.3634 0.041626 11.0834 0.321626 11.0834 0.666626Z"
                   
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4 7.95825H6.58333C6.92833 7.95825 7.20833 7.67825 7.20833 7.33325C7.20833 6.98825 6.92833 6.70825 6.58333 6.70825H4C3.655 6.70825 3.375 6.98825 3.375 7.33325C3.375 7.67825 3.655 7.95825 4 7.95825Z"
                   
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4 11.2916H8.16667C8.51167 11.2916 8.79167 11.0116 8.79167 10.6666C8.79167 10.3216 8.51167 10.0416 8.16667 10.0416H4C3.655 10.0416 3.375 10.3216 3.375 10.6666C3.375 11.0116 3.655 11.2916 4 11.2916Z"
                   
                  />
                </svg>
                <span
                  className={`text-[16px] font-light font-poppins ${
                    pathname === "/reps/meetings"
                      ? "text-white"
                      : "text-[#8E8E8E] group-hover:text-[#1E5EFF]"
                  }`}
                >
                  Meetings{" "}
                </span>
              </div>
            </a>
            {/* Managed in the admin panel and enforced by the API. */}
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        className={`link ${isFindDoctorsEnabled && pathname === "/reps/find-doctors" ? "active" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (isFindDoctorsEnabled) {
                            handleNavigation("/reps/find-doctors");
                          }
                        }}
                      >
                        <div
                          className={`flex group transition-all items-center py-3 px-4 gap-4 rounded-lg ${
                            !isFindDoctorsEnabled
                              ? "opacity-50 cursor-not-allowed"
                              : pathname === "/reps/find-doctors"
                                ? "bg-[#354EF0] text-white cursor-pointer"
                                : "bg-white hover:bg-[#354EF00A] cursor-pointer"
                          }`}
                        >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className={`transition-all flex-shrink-0 ${
                        !isFindDoctorsEnabled
                          ? "fill-[#C5C5C5]"
                          : pathname === "/reps/find-doctors"
                            ? "fill-white"
                            : "fill-[#8E8E8E] group-hover:fill-[#1E5EFF]"
                      }`}
                    >
                      <g clipRule="url(#clip0_1743_3443)">
                        <path
                          d="M19.9723 3.44141C19.9394 3.33506 19.8779 3.23978 19.7946 3.16595C19.7112 3.09211 19.6092 3.04255 19.4997 3.02266L17.3684 2.63203L16.9778 0.500667C16.9577 0.391203 16.9081 0.289314 16.8342 0.206023C16.7604 0.122732 16.6652 0.061209 16.559 0.0281085C16.4527 -0.00499205 16.3394 -0.0084099 16.2314 0.0182246C16.1233 0.0448592 16.0246 0.100532 15.9459 0.179222L12.9633 3.16145C12.8934 3.23135 12.8416 3.31716 12.8122 3.41151C12.7829 3.50586 12.7768 3.60594 12.7947 3.70313L13.2306 6.08043L9.15758 10.1535C9.06696 10.245 9.01627 10.3687 9.0166 10.4976C9.01693 10.6264 9.06826 10.7498 9.15935 10.8409C9.25044 10.932 9.3739 10.9833 9.50272 10.9837C9.63154 10.984 9.75525 10.9333 9.8468 10.8427L13.9197 6.76953L16.2972 7.20543C16.3944 7.22325 16.4945 7.2172 16.5888 7.1878C16.6832 7.15841 16.769 7.10655 16.8388 7.03668L19.8212 4.0543C19.9 3.97572 19.9558 3.87704 19.9824 3.76898C20.0091 3.66092 20.0056 3.54762 19.9723 3.44141ZM16.2848 6.21246L14.7571 5.93239L15.7738 4.91567C15.8194 4.8705 15.8557 4.81676 15.8805 4.75753C15.9053 4.69831 15.9181 4.63477 15.9183 4.57056C15.9185 4.50635 15.9059 4.44274 15.8814 4.38339C15.857 4.32404 15.821 4.27011 15.7756 4.22471C15.7302 4.17931 15.6762 4.14332 15.6169 4.11883C15.5575 4.09433 15.4939 4.0818 15.4297 4.08196C15.3655 4.08212 15.302 4.09497 15.2428 4.11977C15.1835 4.14456 15.1298 4.18082 15.0846 4.22645L14.0679 5.24317L13.7878 3.71539L16.1441 1.35907L16.455 3.055C16.4778 3.17663 16.5368 3.28853 16.6244 3.376C16.7119 3.46346 16.8238 3.52241 16.9455 3.54508L18.6414 3.85602L16.2848 6.21246Z"
                        />
                        <path
                          d="M11.1866 4.43433C11.1989 4.37153 11.1988 4.30692 11.1861 4.24419C11.1735 4.18145 11.1486 4.12183 11.1129 4.06871C11.0772 4.01559 11.0314 3.97002 10.9781 3.93461C10.9248 3.89919 10.865 3.87462 10.8022 3.8623C9.71144 3.64694 8.58437 3.70364 7.52069 4.0274C6.457 4.35115 5.48946 4.93199 4.70361 5.71855C2.06299 8.35918 2.06299 12.6561 4.70361 15.2967C5.73035 16.3233 7.05984 16.9928 8.496 17.2065C9.93216 17.4202 11.399 17.1666 12.6801 16.4833C13.9612 15.8 14.9889 14.7231 15.6114 13.4114C16.234 12.0997 16.4186 10.6226 16.1379 9.198C16.113 9.07118 16.0388 8.95942 15.9316 8.88732C15.8243 8.81522 15.6928 8.78869 15.566 8.81355C15.4391 8.83842 15.3274 8.91264 15.2553 9.01991C15.1832 9.12717 15.1566 9.25868 15.1815 9.3855C15.366 10.3194 15.3175 11.2845 15.0403 12.1952C14.7632 13.1059 14.2659 13.9344 13.5925 14.6073C11.3318 16.8679 7.6535 16.8679 5.39283 14.6073C3.13217 12.3466 3.13217 8.66824 5.39283 6.40758C6.06572 5.73417 6.89416 5.23692 7.80491 4.95981C8.71567 4.68269 9.68066 4.63425 10.6146 4.81875C10.6774 4.83107 10.742 4.8309 10.8047 4.81825C10.8675 4.8056 10.9271 4.78072 10.9802 4.74502C11.0333 4.70933 11.0789 4.66352 11.1143 4.61021C11.1497 4.5569 11.1743 4.49714 11.1866 4.43433Z"
                        />
                        <path
                          d="M18.179 7.9996C18.1166 8.0139 18.0576 8.04035 18.0055 8.07743C17.9533 8.11452 17.909 8.16151 17.875 8.21573C17.841 8.26996 17.818 8.33034 17.8074 8.39345C17.7967 8.45655 17.7986 8.52113 17.8129 8.58351C18.2284 10.3877 18.0455 12.2781 17.2918 13.9692C16.5381 15.6602 15.2546 17.0601 13.6351 17.9573C12.0156 18.8544 10.1481 19.2002 8.3147 18.9423C6.48133 18.6845 4.78167 17.837 3.47248 16.5278C2.1629 15.218 1.31535 13.5174 1.05799 11.6831C0.800617 9.84888 1.14741 7.9807 2.04595 6.36102C2.94448 4.74134 4.34591 3.45822 6.03835 2.70565C7.73079 1.95307 9.62224 1.77196 11.4267 2.18968C11.4891 2.20419 11.5538 2.20624 11.617 2.19574C11.6802 2.18523 11.7407 2.16236 11.795 2.12845C11.8494 2.09453 11.8965 2.05023 11.9337 1.99808C11.971 1.94593 11.9975 1.88695 12.0119 1.82452C12.0263 1.76209 12.0282 1.69744 12.0176 1.63426C12.007 1.57108 11.984 1.51062 11.95 1.45632C11.916 1.40203 11.8716 1.35498 11.8194 1.31786C11.7672 1.28074 11.7082 1.25428 11.6457 1.23999C10.0744 0.87569 8.43624 0.917652 6.88566 1.36192C5.33508 1.80619 3.92322 2.63812 2.78327 3.77925C1.75592 4.80728 0.97809 6.05727 0.509614 7.43307C0.041138 8.80886 -0.105497 10.2738 0.0809889 11.7151C0.267475 13.1565 0.782113 14.5359 1.58531 15.7471C2.38851 16.9584 3.45885 17.9693 4.71401 18.702C5.96916 19.4347 7.37567 19.8697 8.82532 19.9736C10.275 20.0774 11.7291 19.8474 13.0759 19.3011C14.4227 18.7548 15.6262 17.9069 16.5939 16.8225C17.5616 15.7381 18.2676 14.4462 18.6577 13.0462C19.0799 11.5188 19.116 9.91046 18.7629 8.36566C18.7486 8.30328 18.7221 8.24433 18.685 8.19218C18.6479 8.14003 18.601 8.09569 18.5467 8.0617C18.4925 8.02771 18.4321 8.00473 18.369 7.99407C18.3059 7.98342 18.2413 7.9853 18.179 7.9996Z"
                        />
                        <path
                          d="M9.79462 7.51021C9.92298 7.52266 10.051 7.48372 10.1507 7.40192C10.2505 7.32013 10.3137 7.20215 10.3266 7.07383C10.3394 6.94552 10.3009 6.81733 10.2195 6.71734C10.138 6.61736 10.0203 6.55373 9.89201 6.54041C9.07258 6.45774 8.24765 6.63079 7.53052 7.03578C6.81339 7.44076 6.23921 8.05783 5.88686 8.80224C5.5345 9.54665 5.42124 10.3819 5.56263 11.1932C5.70402 12.0046 6.09313 12.7523 6.67652 13.3336C7.2599 13.915 8.00896 14.3015 8.82081 14.44C9.63266 14.5785 10.4675 14.4623 11.2107 14.1074C11.9538 13.7524 12.5689 13.1761 12.9713 12.4575C13.3738 11.739 13.544 10.9134 13.4584 10.0943C13.4518 10.0307 13.4327 9.96894 13.4022 9.91266C13.3718 9.85638 13.3305 9.80666 13.2808 9.76633C13.2311 9.726 13.174 9.69585 13.1126 9.67761C13.0513 9.65936 12.987 9.65338 12.9233 9.66C12.8597 9.66661 12.7979 9.6857 12.7417 9.71618C12.6854 9.74665 12.6357 9.78791 12.5953 9.8376C12.555 9.88728 12.5249 9.94443 12.5066 10.0058C12.4884 10.0671 12.4824 10.1314 12.489 10.1951C12.5538 10.8141 12.4253 11.438 12.1212 11.981C11.8171 12.5241 11.3524 12.9597 10.7908 13.228C10.2292 13.4963 9.59831 13.5841 8.98479 13.4794C8.37127 13.3748 7.80521 13.0827 7.36435 12.6434C6.92349 12.204 6.62946 11.639 6.52265 11.0258C6.41583 10.4127 6.50148 9.78151 6.76781 9.21899C7.03415 8.65647 7.46811 8.19021 8.0101 7.88424C8.55209 7.57827 9.17551 7.44762 9.79474 7.51021H9.79462Z"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1743_3443">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span
                      className={`text-[16px] font-light font-poppins flex-1 ${
                        !isFindDoctorsEnabled
                          ? "text-[#C5C5C5]"
                          : pathname === "/reps/find-doctors"
                            ? "text-white"
                            : "text-[#8E8E8E] group-hover:text-[#1E5EFF]"
                      }`}
                    >
                      Find Doctors
                    </span>
                    <span className={`text-[10px] font-semibold font-poppins px-2 py-0.5 rounded-full ${
                      pathname === "/reps/find-doctors" && isFindDoctorsEnabled
                        ? "text-white bg-white/20"
                        : "text-[#354EF0] bg-[#354EF0]/10"
                    }`}>
                      PRO
                    </span>

                        </div>
                      </a>
                    </TooltipTrigger>
                    {!isFindDoctorsEnabled && (
                      <TooltipContent side="top" align="center" sideOffset={8} className="!shadow-none !border-0 drop-shadow-md">
                        <p className="text-xs font-medium">{checkingAccess ? "Checking access…" : accessError ? "Could not check access. Try refreshing." : "Contact Alpha.MD to enable access"}</p>
                        <TooltipArrow className="fill-popover" width={12} height={6} />
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

            <a
              className={`link ${
                pathname === "/reps/favorites" ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("/reps/favorites");
              }}
            >
              <div
                className={`flex  group transition-all cursor-pointer items-center py-3 px-4 gap-4 rounded-lg ${
                  pathname === "/reps/favorites"
                    ? "bg-[#354EF0] text-white"
                    : "bg-white hover:bg-[#354EF00A]"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transition-all ${
                    pathname === "/reps/favorites"
                      ? "fill-white"
                      : "fill-[#8E8E8E] group-hover:fill-[#1E5EFF]"
                  }`}
                >
                  <g clipRule="url(#clip0_1743_3451)">
                    <path
                      d="M18.2156 2.91841C17.063 1.76582 15.5306 1.1311 13.9006 1.1311C12.459 1.1311 11.0937 1.62765 9.99997 2.53971C8.90634 1.62765 7.54099 1.1311 6.09944 1.1311C4.46945 1.1311 2.93702 1.76585 1.78442 2.91841C-0.594808 5.29768 -0.594808 9.1691 1.78442 11.5484L8.47575 18.2398C8.88168 18.6456 9.42297 18.8691 10 18.8691C10.577 18.8691 11.1183 18.6456 11.5243 18.2397L18.2156 11.5484C20.5948 9.1691 20.5948 5.29764 18.2156 2.91841ZM17.3867 10.7196L10.6954 17.4108C10.5109 17.5953 10.264 17.6969 10 17.6969C9.73607 17.6969 9.48912 17.5953 9.30462 17.4109L2.61333 10.7196C0.691145 8.79727 0.691144 5.66951 2.61329 3.74728C3.5445 2.81608 4.78258 2.30327 6.09944 2.30327C7.4163 2.30327 8.65439 2.81608 9.58556 3.74724C9.81437 3.97613 10.1855 3.97613 10.4144 3.74724C11.3456 2.81604 12.5837 2.30323 13.9006 2.30323C15.2175 2.30323 16.4556 2.81604 17.3867 3.7472C19.3089 5.66951 19.3089 8.79727 17.3867 10.7196Z"
                    
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1743_3451">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span
                  className={`text-[16px] font-light font-poppins ${
                    pathname === "/reps/favorites"
                      ? "text-white"
                      : "text-[#8E8E8E] group-hover:text-[#1E5EFF]"
                  }`}
                >
                  Favorites{" "}
                </span>
              </div>{" "}
            </a>
            <a
              className={`link ${
                pathname.startsWith("/reps/my-products") ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("/reps/my-products");
              }}
            >
              <div
                className={`flex  transition-all group cursor-pointer items-center py-3 px-4 gap-4 rounded-lg ${
                  pathname.startsWith("/reps/my-products")
                    ? "bg-[#354EF0] text-white"
                    : "bg-white hover:bg-[#354EF00A]"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transition-all ${
                    pathname === "/reps/my-products"
                      ? "fill-white"
                      : "fill-[#8E8E8E] group-hover:fill-[#1E5EFF]"
                  }`}
                >
                  <path
                    d="M18.2125 5.10367L10.7169 1.89742C10.255 1.69867 9.74625 1.69805 9.28563 1.8968L1.7875 5.10367C1.11937 5.3893 0.6875 6.04305 0.6875 6.77055V13.2268C0.6875 13.9474 1.11688 14.6005 1.78563 14.8937L9.28375 18.0987C9.515 18.1987 9.75812 18.2487 10.0006 18.2487C10.2431 18.2487 10.485 18.1987 10.715 18.0999L18.22 14.8918C18.8844 14.6005 19.3131 13.9474 19.3131 13.2268V6.77055C19.3125 6.04305 18.8806 5.3893 18.2125 5.10367ZM9.72875 2.93055C9.8175 2.89242 9.90812 2.87305 9.99937 2.87305C10.0906 2.87305 10.1831 2.89242 10.2731 2.93117L17.3081 5.94055L14.3394 7.2118L7.21812 4.00492L9.72875 2.93055ZM10.2794 8.94992C10.0994 9.02492 9.90063 9.0243 9.7275 8.95305L2.69188 5.94055L5.8125 4.60555L12.9344 7.81305L10.2794 8.94992ZM2.23188 13.8612C1.97688 13.7499 1.8125 13.5005 1.8125 13.2268V6.78742L9.29187 9.98992C9.33937 10.0093 9.38875 10.0187 9.43687 10.0337V16.9412L2.23188 13.8612ZM17.7731 13.8593L10.5625 16.9418V10.0337C10.6131 10.0174 10.665 10.008 10.715 9.98742L18.1875 6.78742V13.2262C18.1875 13.5005 18.0231 13.7499 17.7731 13.8593Z"
                  
                  />
                  <path
                    d="M4.95022 12.8306L3.45334 12.1913C3.16834 12.07 2.83709 12.2019 2.71522 12.4875C2.59334 12.7731 2.72584 13.1038 3.01147 13.2256L4.50834 13.865C4.58022 13.8956 4.65522 13.9106 4.72959 13.9106C4.94772 13.9106 5.15584 13.7825 5.24647 13.5688C5.36834 13.2831 5.23584 12.9525 4.95022 12.8306Z"
                   
                  />
                </svg>
                <span
                  className={`text-[16px] font-light font-poppins ${
                    pathname.startsWith("/reps/my-products")
                      ? "text-white"
                      : "text-[#8E8E8E] group-hover:text-[#1E5EFF]"
                  }`}
                >
                  My Products{" "}
                </span>
              </div>
            </a>
          </div>
          <div className="p-3 mt-8 bg-neutral-50 flex-col justify-center items-start gap-3 inline-flex">
            <div className="self-stretch flex-col justify-start items-start gap-1 inline-flex">
              <div className="text-black text-sm font-semibold font-poppins">
                Need help?
              </div>
              <div className="text-neutral-400 text-xs font-normal font-poppins">
                We’re here to help you in case you’re stuck
              </div>
            </div>
            <a onClick={(e) => {
                e.preventDefault();
                handleNavigation("/contact-us");
              }}>
              <Button
                textColor={"#354EF0"}
                border={"1px solid #354EF0"}
                fontSize={"14px"}
                borderRadius={"10px"}
                fontWeight={"500"}
                padding={"8px 40px"}
              >
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default RepsSidebar;
